import { useSelector, useDispatch } from 'react-redux';
import selectors from 'store/selectors';
import CustomDialog from 'components/CustomDialog/customDialog';
import { setDialog } from 'store/actions/dialogActions';
import {
  AddThresholdButton,
  ColorPickerContainer,
  ColorSquare,
  ContentCnt,
  DragHandle,
  ThresholdContainer,
  ThresholdHeader,
  ThresholdRow,
} from './style';
import CustomDialogHeader from 'components/CustomDialog/DialogHeader/dialogHeader.cmp';
import DIALOGS from 'utils/dialogIds';
import { Box, Typography, IconButton } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import CustomInput from 'components/Form/TextField/TextField.cmp';
import {
  GridReportViewThreshold,
  GridReportViewThresholdComparator,
} from 'components/GridReport/models';
import {
  checkThresholdOverlap,
  validateThresholds,
  ThresholdItem,
} from 'utils/thresholdUtils';
import ActionDropdown from 'components/Dropdown/ActionDropdown/ActionDropdown';
import RemoveIcon from '@mui/icons-material/Remove';
import useGridReportView from 'components/GridReport/hooks/useGridReportView';
import ColorPicker from 'components/ColorPicker';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const selectedStyles = {
  listItemProps: {
    styleSelected: true,
    selectedStyles: {
      padding: '7px 16px',
      background: 'rgb(227, 227, 255)',
      color: '#4E48FA',
      '& .endIcon': {
        background: '#6868FE',
        borderColor: '#6868FE',
        color: '#ffffff',
      },
    },
  },
};
// Predefined colors that match the image
const predefinedColors = [
  '#4CAF50', // Green
  '#EF5350', // Red
  '#FF8F00', // Orange
  '#8D6E63', // Brown
  '#26A69A', // Teal
  '#29B6F6', // Light Blue
  '#5C6BC0', // Indigo
  '#7E57C2', // Purple
  '#EC407A', // Pink
  '#C0CA33', // Lime
  '#26C6DA', // Cyan
  '#78909C', // Blue Grey
];

const comparatorOptions = [
  { label: 'is greater than or equal to', id: 'gte', ...selectedStyles },
  { label: 'is greater than', id: 'gt', ...selectedStyles },
  { label: 'is less than or equal to', id: 'lte', ...selectedStyles },
  { label: 'is less than', id: 'lt', ...selectedStyles },
  { label: 'is equal to', id: 'eq', ...selectedStyles },
  { label: 'is between', id: 'btn', ...selectedStyles },
];

// Get color name for display
const getColorName = (hexColor: string) => {
  const colorNames: Record<string, string> = {
    '#4CAF50': 'Green',
    '#EC407A': 'Pink',
    '#FF8F00': 'Orange',
    '#8D6E63': 'Brown',
    '#26A69A': 'Teal',
    '#29B6F6': 'Light Blue',
    '#5C6BC0': 'Indigo',
    '#7E57C2': 'Purple',
    '#EF5350': 'Red',
    '#C0CA33': 'Lime',
    '#26C6DA': 'Cyan',
    '#78909C': 'Blue Grey',
  };

  return colorNames[hexColor] || hexColor;
};

// Generate a unique ID
const generateId = () => {
  return `threshold_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const GridReportThresholdsDialog = () => {
  const dialogState = useSelector(selectors.getDialogState);
  const { open, data = {} } =
    dialogState?.[DIALOGS?.GRID_REPORT_THRESHOLDS_DIALOG] || {};

  const {
    createGridReportViewThreshold,
    updateGridReportViewThreshold,
    deleteGridReportViewThreshold,
    gridReportThresholds,
    gridReportViewId,
  } = data;

  const [thresholds, setThresholds] = useState<ThresholdItem[]>([]);
  const [changedThresholds, setChangedThresholds] = useState<Set<string>>(
    new Set(),
  );
  const [colorOpen, setColorOpen] = useState<boolean>(false);
  const [activeColorIndex, setActiveColorIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [overlapError, setOverlapError] = useState<string>('');

  const colorPickerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { getGridReportViewThresholds } = useGridReportView(gridReportViewId);

  // Initialize thresholds from props
  useEffect(() => {
    if (gridReportThresholds?.length) {
      setThresholds(gridReportThresholds);
    } else {
      // Add default threshold if no thresholds exist
      const defaultThreshold: ThresholdItem = {
        color: '#4CAF50', // Green
        label: 'Green',
        comparator: 'btn',
        value: 50,
        secondValue: 100,
        tempId: generateId(),
      };
      setThresholds([defaultThreshold]);
    }
  }, [gridReportThresholds]);

  // Validate thresholds whenever they change
  useEffect(() => {
    if (thresholds.length > 0) {
      const { errors, overlapError } = validateThresholds(thresholds);
      setValidationErrors(errors);
      setOverlapError(overlapError);
    }
  }, [thresholds]);

  const handleClose = () => {
    setChangedThresholds(new Set());
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS?.GRID_REPORT_THRESHOLDS_DIALOG,
      }),
    );
  };

  const handleSave = async () => {
    const { isValid, errors, overlapError } = validateThresholds(thresholds);
    setValidationErrors(errors);
    setOverlapError(overlapError);
    if (!isValid) {
      return;
    }

    // Save all thresholds
    for (const threshold of thresholds) {
      const { tempId, ...thresholdData } = threshold;

      if (!threshold.id) {
        // New threshold - add required properties
        const newThreshold = {
          ...thresholdData,
          GridReportViewId: gridReportViewId,
          color: thresholdData.color || predefinedColors[0],
          value: thresholdData.value as number,
          label: thresholdData.label || '',
          comparator:
            (thresholdData.comparator as GridReportViewThresholdComparator) ||
            'gte',
        };
        await createGridReportViewThreshold(newThreshold);
      } else if (changedThresholds.has(threshold.id)) {
        // Update existing threshold that has been changed
        const updateData = {
          color: thresholdData.color,
          label: thresholdData.label,
          comparator:
            thresholdData.comparator as GridReportViewThresholdComparator,
          value: thresholdData.value as number,
          secondValue: thresholdData.secondValue as number | undefined,
        };
        await updateGridReportViewThreshold(threshold.id, updateData);
      }
    }

    const newThresholds = await getGridReportViewThresholds();
    setThresholds(newThresholds);
    setChangedThresholds(new Set());
  };

  const handleDelete = async (index: number) => {
    const threshold = thresholds[index];

    if (threshold.id) {
      await deleteGridReportViewThreshold(threshold.id);
      const newThresholds = await getGridReportViewThresholds();
      setThresholds(newThresholds);
    } else {
      // For unsaved thresholds, just remove from state
      const newThresholds = [...thresholds];
      newThresholds.splice(index, 1);
      setThresholds(newThresholds);
    }
  };

  const handleAddThreshold = () => {
    const newThreshold: ThresholdItem = {
      color: predefinedColors[0],
      label: getColorName(predefinedColors[0]),
      comparator: 'gte',
      value: 0,
      tempId: generateId(),
    };

    setThresholds([...thresholds, newThreshold]);
  };

  const handleUpdateThreshold = (
    index: number,
    field: keyof ThresholdItem,
    value: any,
  ) => {
    const newThresholds = [...thresholds];
    newThresholds[index] = { ...newThresholds[index], [field]: value };

    // Track that this threshold has been changed
    const thresholdId = newThresholds[index].id || newThresholds[index].tempId;
    if (thresholdId && newThresholds[index].id) {
      setChangedThresholds((prev) => new Set([...prev, thresholdId]));
    }

    // If color is updated, also update the label if it matches a color name
    if (field === 'color') {
      const currentLabel = newThresholds[index].label;
      const oldColor = thresholds[index].color || predefinedColors[0];
      const oldColorName = getColorName(oldColor);

      if (currentLabel === oldColorName) {
        newThresholds[index].label = getColorName(value);
      }
    }

    // If comparator is changed to/from "between", handle the second value
    if (field === 'comparator') {
      if (value === 'btn' && !newThresholds[index].secondValue) {
        // Initialize secondValue when switching to "between"
        newThresholds[index].secondValue = '';
      }
    }

    setThresholds(newThresholds);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(thresholds);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setThresholds(items);
  };

  const handleColorClick = (
    index: number,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setActiveColorIndex(index);
    setAnchorEl(event.currentTarget);
    setColorOpen(true);
  };

  const handleColorSelect = (color: string) => {
    if (activeColorIndex !== null) {
      handleUpdateThreshold(activeColorIndex, 'color', color);
    }
    setColorOpen(false);
    setActiveColorIndex(null);
    setAnchorEl(null);
  };

  return (
    <CustomDialog
      header={
        <CustomDialogHeader
          other={{ sx: { alignItems: 'start' } }}
          onClose={handleClose}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography fontSize={22} fontWeight={800} sx={{ mt: 2 }}>
              Grid Report Thresholds
            </Typography>
          </Box>
        </CustomDialogHeader>
      }
      actionButtonCntProps={{
        primaryBtnProps: {
          onClick: async () => await handleSave(),
          disabled:
            thresholds.length === 0 ||
            Object.keys(validationErrors).length > 0 ||
            overlapError !== '',
        },
        secondaryBtnProps: { onClick: handleClose },
      }}
      primaryBtnText="Save Thresholds"
      dialogProps={{
        open: open,
        fullWidth: true,
        onClose: handleClose,
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: '720px',
          },
        },
      }}
    >
      <ContentCnt>
        <ThresholdContainer>
          <ThresholdHeader>
            <Typography
              width={'13%'}
              pl={'12px'}
              minWidth={'13%'}
              variant="body2"
            >
              Color
            </Typography>
            <Typography width={'22%'} minWidth={'22%'} variant="body2">
              Label
            </Typography>
            <Typography variant="body2">Threshold</Typography>
          </ThresholdHeader>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="thresholds">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {thresholds.map((threshold, index) => {
                    const id =
                      threshold.id || threshold.tempId || index.toString();
                    return (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(provided) => (
                          <ThresholdRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            {/* color */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '13%',
                                minWidth: '13%',
                              }}
                            >
                              <div {...provided.dragHandleProps}>
                                <DragHandle>
                                  <DragIndicatorIcon />
                                </DragHandle>
                              </div>
                              <div>
                                <ColorPickerContainer
                                  onClick={(event) =>
                                    handleColorClick(index, event)
                                  }
                                >
                                  <ColorSquare
                                    sx={{ backgroundColor: threshold.color }}
                                  />
                                  <KeyboardArrowDownIcon
                                    sx={{ fontSize: 16, color: '#666' }}
                                  />
                                </ColorPickerContainer>
                              </div>
                            </Box>
                            {/* label */}
                            <Box width={'22%'} minWidth={'22%'}>
                              <CustomInput
                                sx={{
                                  '& .MuiInputBase-root': {
                                    height: '36px',
                                  },
                                  '& .MuiOutlinedInput-input': { fontSize: 14 },
                                }}
                                suppressErrorSpace={true}
                                muiLabel={false}
                                fieldProps={{
                                  placeholder: 'Enter label',
                                  onChange: (e) =>
                                    handleUpdateThreshold(
                                      index,
                                      'label',
                                      e.target.value,
                                    ),
                                  fullWidth: true,
                                  value: threshold.label || '',
                                  error: !!validationErrors[`${id}_label`],
                                  styles: {
                                    borderRadius: 8,
                                    backgroundColor: '#E0E0E0',
                                  },
                                }}
                              />
                            </Box>
                            {/* threshold */}
                            <Box
                              display={'flex'}
                              flex={1}
                              gap={2}
                              alignItems={'center'}
                            >
                              <Box sx={{ flex: 1 }}>
                                <ActionDropdown
                                  options={comparatorOptions}
                                  handleOptionSelect={(option) =>
                                    handleUpdateThreshold(
                                      index,
                                      'comparator',
                                      option.id as GridReportViewThresholdComparator,
                                    )
                                  }
                                  iconButton={false}
                                  buttonProps={{
                                    variant: 'outlined',
                                    endIcon: <KeyboardArrowDownIcon />,
                                    sx: {
                                      padding: '4px 10px 4px 14px',
                                      height: 36,
                                      fontWeight: 400,
                                      borderRadius: '6px',
                                      fontSize: 14,
                                      lineHeight: '24px',
                                      letterSpacing: '0.4px',
                                      justifyContent: 'space-between',
                                      color: '#000000',
                                      border: '1px solid #E0E0E0',
                                      minWidth: '150px',
                                      width: '100%',
                                    },
                                  }}
                                >
                                  {comparatorOptions.find(
                                    (option) =>
                                      option.id === threshold.comparator,
                                  )?.label || 'Select condition'}
                                </ActionDropdown>
                              </Box>

                              {threshold.comparator === 'btn' ? (
                                <>
                                  <CustomInput
                                    sx={{
                                      flex: '0 1 80px',
                                      width: '80px',
                                      '& .MuiInputBase-root': {
                                        height: '36px',
                                        width: '80px',
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: 14,
                                      },
                                    }}
                                    suppressErrorSpace={true}
                                    muiLabel={false}
                                    fieldProps={{
                                      placeholder: 'Min',
                                      onChange: (e) =>
                                        handleUpdateThreshold(
                                          index,
                                          'value',
                                          e.target.value === ''
                                            ? ''
                                            : parseFloat(e.target.value),
                                        ),
                                      value: threshold.value ?? '',
                                      type: 'number',
                                      error: !!validationErrors[`${id}_value`],
                                      styles: {
                                        borderRadius: 8,
                                        backgroundColor: '#E0E0E0',
                                      },
                                    }}
                                  />
                                  <Typography variant="body2">-</Typography>
                                  <CustomInput
                                    sx={{
                                      flex: '0 1 80px',
                                      width: '80px',
                                      '& .MuiInputBase-root': {
                                        height: '36px',
                                        width: '80px',
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: 14,
                                      },
                                    }}
                                    suppressErrorSpace={true}
                                    muiLabel={false}
                                    fieldProps={{
                                      placeholder: 'Max',
                                      onChange: (e) =>
                                        handleUpdateThreshold(
                                          index,
                                          'secondValue',
                                          e.target.value === ''
                                            ? ''
                                            : parseFloat(e.target.value),
                                        ),
                                      value: threshold.secondValue ?? '',
                                      type: 'number',
                                      error:
                                        !!validationErrors[`${id}_secondValue`],
                                      styles: {
                                        borderRadius: 8,
                                        backgroundColor: '#E0E0E0',
                                      },
                                    }}
                                  />
                                </>
                              ) : (
                                <CustomInput
                                  sx={{
                                    flex: '0 1 80px',
                                    width: '80px',
                                    '& .MuiInputBase-root': {
                                      height: '36px',
                                      width: '80px',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      fontSize: 14,
                                    },
                                  }}
                                  suppressErrorSpace={true}
                                  muiLabel={false}
                                  fieldProps={{
                                    placeholder: 'Value',
                                    onChange: (e) =>
                                      handleUpdateThreshold(
                                        index,
                                        'value',
                                        e.target.value === ''
                                          ? ''
                                          : parseFloat(e.target.value),
                                      ),
                                    value: threshold.value ?? '',
                                    type: 'number',
                                    error: !!validationErrors[`${id}_value`],
                                    styles: {
                                      borderRadius: 8,
                                      backgroundColor: '#E0E0E0',
                                    },
                                  }}
                                />
                              )}
                              {thresholds.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(index)}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                      color: '#f44336',
                                    },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </ThresholdRow>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Box sx={{ mt: 2 }}>
            <AddThresholdButton
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddThreshold}
            >
              Add threshold
            </AddThresholdButton>
          </Box>
        </ThresholdContainer>

        {overlapError && (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #f44336',
              mb: 1,
              mx: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {overlapError}
            </Typography>
          </Box>
        )}

        {/* Color Picker Popper */}
        {activeColorIndex !== null && anchorEl && (
          <div ref={colorPickerRef}>
            <ColorPicker
              anchorEl={anchorEl}
              open={colorOpen}
              value={
                (thresholds[activeColorIndex]?.color ||
                  predefinedColors[0]) as string
              }
              onClose={() => {
                setColorOpen(false);
                setActiveColorIndex(null);
                setAnchorEl(null);
              }}
              onSelect={handleColorSelect}
              colors={predefinedColors}
            />
          </div>
        )}
      </ContentCnt>
    </CustomDialog>
  );
};

export default GridReportThresholdsDialog;
